import * as React from 'react';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { ReleaseStageMember, ReleaseStageMembersResponse } from './ReleaseStageMembers.types';

export interface UseReleaseStageMembersResult {
  members: ReleaseStageMember[];
  loading: boolean;
  failed: boolean;
  refreshing: boolean;
  /**
   * Asks for the members again. Nothing else does: the view is read when the dialog
   * opens and when the reader says so, never on a timer behind their back.
   */
  refresh: () => void;
  updateLockStatuses: (updates: LockStatusUpdate[]) => void;
}

export interface LockStatusUpdate {
  TenantId: string;
  Locked?: boolean;
}

/**
 * Loads the members of a release stage from the release stage job service,
 * which answers the whole membership already enriched with the state of each
 * instance, in a single call.
 */
export const useReleaseStageMembers = (
  releaseStageId: string,
  enabled: boolean,
): UseReleaseStageMembersResult => {
  const { fetch: fetchMembers } = useRestApi<ReleaseStageMembersResponse>(
    apiUrl('lct', `releaseStageJob/${releaseStageId}/members`),
    { manual: true },
  );

  const [members, setMembers] = React.useState<ReleaseStageMember[]>([]);
  const [outcome, setOutcome] = React.useState<'pending' | 'ready' | 'failed'>('pending');

  /** Bumped to ask for the members again. */
  const [refreshes, setRefreshes] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);

  const refresh = React.useCallback(() => {
    setRefreshes((previous) => previous + 1);
  }, []);

  React.useEffect(() => {
    let isCanceled = false;

    if (!enabled) {
      setMembers([]);
      setOutcome('pending');
      setRefreshes(0);
      setRefreshing(false);
      return undefined;
    }

    const loadMembers = async () => {
      setMembers([]);
      setOutcome('pending');
      if (refreshes > 0) {
        setRefreshing(true);
      }

      try {
        const response = await fetchMembers();
        const items = response?.data?.items;

        if (isCanceled) {
          return;
        }

        setMembers(Array.isArray(items) ? items : []);
        setOutcome('ready');
      } catch {
        if (!isCanceled) {
          setOutcome('failed');
        }
      } finally {
        if (!isCanceled) {
          setRefreshing(false);
        }
      }
    };

    loadMembers();

    return () => {
      isCanceled = true;
    };
  }, [enabled, fetchMembers, refreshes, releaseStageId]);

  const updateLockStatuses = React.useCallback((updates: LockStatusUpdate[]) => {
    const statusByTenantId = new Map(updates.map(({ TenantId, Locked }) => [TenantId, Locked]));
    setMembers((current) =>
      current.map((member) =>
        statusByTenantId.has(member.TenantId)
          ? { ...member, Locked: statusByTenantId.get(member.TenantId) }
          : member,
      ),
    );
  }, []);

  return {
    members,
    loading: enabled && outcome === 'pending',
    failed: outcome === 'failed',
    refreshing,
    refresh,
    updateLockStatuses,
  };
};
